import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";

import { InvalidOperationException } from "../../exceptions/exceptions";
import { QueryStringParameterKey } from "../../routes/shared/shared-route.constant";
import { DataAnalyticsCampaign } from "../../store/quote/term-life-insurance/quote.state";
import { CookieName, CustomCookieService } from "../custom-cookie.service";

@Injectable({ providedIn: "root" })
export class DataAnalyticsExtractorService {
  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _customCookieService: CustomCookieService,
  ) {}

  hasAtLeastOneUtmParameterInUrl(url: URL) {
    const canAtLeastOneParameterInUrlBeFound =
      this.canAtLeastOneUtmParameterInUrlBeFound(url);

    return canAtLeastOneParameterInUrlBeFound;
  }

  hasAtLeastOneUtmParameterInCookieUrl() {
    const encodedCampaignUrl = this._customCookieService.get(
      CookieName.CAMPAIGN_URL,
    );

    if (!encodedCampaignUrl) {
      return false;
    }

    const canCampaignUrlBeParsed = URL.canParse(encodedCampaignUrl);

    if (!canCampaignUrlBeParsed) {
      return false;
    }

    const parsedCampaignUrl = new URL(encodedCampaignUrl);

    const canAtLeastOneParameterInCookieUrlBeFound =
      this.canAtLeastOneUtmParameterInUrlBeFound(parsedCampaignUrl);

    return canAtLeastOneParameterInCookieUrlBeFound;
  }

  private canAtLeastOneUtmParameterInUrlBeFound(url: URL) {
    const searchParams = url.searchParams;

    const canUtmSourceBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.UTM_SOURCE,
    );

    const canUtmMediumBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.UTM_MEDIUM,
    );

    const canUtmCampaignBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.UTM_CAMPAIGN,
    );

    const canUtmContentBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.UTM_CONTENT,
    );

    const canUtmTermBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.UTM_TERM,
    );

    const canUtmIdBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.UTM_ID,
    );

    const canGclIdBeFound = this.canParameterInUrlBeFound(
      searchParams,
      QueryStringParameterKey.GCL_ID,
    );

    const canAtLeastOneParameterBeFound =
      canUtmSourceBeFound ||
      canUtmMediumBeFound ||
      canUtmCampaignBeFound ||
      canUtmContentBeFound ||
      canUtmTermBeFound ||
      canUtmIdBeFound ||
      canGclIdBeFound;

    return canAtLeastOneParameterBeFound;
  }

  throwExceptionIfQuoteIdExists() {
    const url = new URL(this._document.location.href);
    const quoteId = url.searchParams.get(QueryStringParameterKey.QUOTE_ID);

    if (quoteId) {
      throw new InvalidOperationException(
        `It should not be possible that the '${QueryStringParameterKey.QUOTE_ID}' is set, while there are also utm campaign parameters present`,
      );
    }
  }

  getDataAnalyticsCampaignFromUrl() {
    const searchParams = new URL(this._document.location.href).searchParams;
    return this.createDataAnalyticsCampaign(searchParams, undefined);
  }

  tryGetDataAnalyticsCampaignFromCookieUrl() {
    const encodedCampaignUrl = this._customCookieService.get(
      CookieName.CAMPAIGN_URL,
    );

    if (!encodedCampaignUrl) {
      throw new InvalidOperationException(
        `At this point the value from the cookie should never be empty and means that we did not correctly check for the '${CookieName.CAMPAIGN_URL}'`,
      );
    }

    const canCampaignUrlBeParsed = URL.canParse(encodedCampaignUrl);

    /**
     * Unfortunately, we do not control what urls are being set. For that reason we return 'undefined' if we can not parse it.
     */
    if (!canCampaignUrlBeParsed) {
      return undefined;
    }

    const urlSearchParams = new URL(encodedCampaignUrl).searchParams;

    /**
     * A referrer does not necessarily have to be a valid {@link URL}.
     * This is why we use a string instead of a {@link URL}.
     */
    const referrer = this._customCookieService.get(CookieName.REFERRER);

    return this.createDataAnalyticsCampaign(urlSearchParams, referrer);
  }

  /**
   * Any type of combination of utm parameters can exist.
   * They are all treated as optional.
   * It's enough if at least one utm parameter can be found.
   */
  private createDataAnalyticsCampaign(
    params: URLSearchParams,
    referrer: string | undefined,
  ) {
    const dataAnalyticsCampaign: DataAnalyticsCampaign = {
      utmSource: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.UTM_SOURCE,
      ),
      utmMedium: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.UTM_MEDIUM,
      ),
      utmCampaign: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.UTM_CAMPAIGN,
      ),
      utmContent: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.UTM_CONTENT,
      ),
      utmTerm: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.UTM_TERM,
      ),
      utmId: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.UTM_ID,
      ),
      gclId: this.tryGetCampaignParameter(
        params,
        QueryStringParameterKey.GCL_ID,
      ),
      referrer: referrer,
    };

    return dataAnalyticsCampaign;
  }

  private tryGetCampaignParameter(
    urlSearchParams: URLSearchParams,
    campaignParameter: QueryStringParameterKey,
  ) {
    return urlSearchParams.get(campaignParameter) ?? undefined;
  }

  private canParameterInUrlBeFound(
    params: URLSearchParams,
    search: QueryStringParameterKey,
  ) {
    return params.has(search);
  }
}
